import z from 'zod';
import { makeSlugUnique, stringToSlugStartingWithPrefix } from '../../utils/slug';
import {
  CUSTOMER_GROUP_BASE_SLUG,
  CUSTOMER_GROUP_SLUG_MAX_LENGTH,
  DEFAULT_SETTINGS,
} from './constants';
import type { CustomerGroupBuilderState } from './types/customer-group.schema';
import type { CustomerGroupQuickSetupState } from './types/customer-group-quick-setup.schema';
import {
  QUICK_SETUP_MIN_ORDER_REQUIREMENTS,
  QUICK_SETUP_PAYMENT_METHODS,
} from './types/customer-group-quick-setup.schema';

export const QUICK_SETUP_DRAFT_QUERY_KEY = 'quickSetupDraft';

const quickSetupDraftSchema = z.object({
  name: z.string(),
  baseCurrency: z.string(),
  assignedPriceList: z.string(),
  paymentMethods: z.array(z.enum(QUICK_SETUP_PAYMENT_METHODS)),
  minOrderRequirements: z.array(z.enum(QUICK_SETUP_MIN_ORDER_REQUIREMENTS)),
  minOrderValue: z.string().optional(),
  minOrderUnits: z.string().optional(),
});

type QuickSetupDraft = z.infer<typeof quickSetupDraftSchema>;

export function buildQuickSetupAdvancedQuery(
  group: CustomerGroupQuickSetupState,
): Record<string, string> {
  return { [QUICK_SETUP_DRAFT_QUERY_KEY]: JSON.stringify(group) };
}

export function parseQuickSetupDraft(
  query: Record<string, string>,
  existingSlugs: string[],
): CustomerGroupBuilderState | undefined {
  const raw = query[QUICK_SETUP_DRAFT_QUERY_KEY];
  if (!raw) {
    return undefined;
  }

  let parsedJson: unknown;
  try {
    parsedJson = JSON.parse(raw);
  } catch {
    return undefined;
  }

  const result = quickSetupDraftSchema.safeParse(parsedJson);
  if (!result.success) {
    return undefined;
  }

  return mapQuickSetupDraftToBuilderState(result.data, existingSlugs);
}

function mapQuickSetupDraftToBuilderState(
  draft: QuickSetupDraft,
  existingSlugs: string[],
): CustomerGroupBuilderState {
  return {
    name: draft.name,
    // Mirrors the slug generation in the quick setup submit handler, so a transferred
    // draft can't collide with a customer group that already exists.
    slug: makeSlugUnique(
      stringToSlugStartingWithPrefix(draft.name, {
        maxLength: CUSTOMER_GROUP_SLUG_MAX_LENGTH,
      }),
      existingSlugs,
    ),
    parent_slug: CUSTOMER_GROUP_BASE_SLUG,
    hasChildren: false,
    settings: {
      ...DEFAULT_SETTINGS,
      ...(draft.assignedPriceList
        ? { priceListSelection: [{ price_list_slug: draft.assignedPriceList }] }
        : {}),
      ...(draft.paymentMethods.length > 0
        ? {
            allowedPaymentMethods: {
              upfront_payment: draft.paymentMethods.includes('upfront_payment'),
              payment_on_account: draft.paymentMethods.includes('payment_on_account'),
              payment_by_invoice: draft.paymentMethods.includes('payment_by_invoice'),
              quote: false,
            },
          }
        : {}),
      ...(draft.minOrderRequirements.includes('value_based_min')
        ? {
            orderTotalsValidation: [
              {
                currency_code: draft.baseCurrency,
                minimum_value: draft.minOrderValue,
                compare_type: 'net',
              },
            ],
          }
        : {}),
      ...(draft.minOrderRequirements.includes('order_based_min')
        ? {
            orderItemQuantityValidation: {
              minimum_quantity: draft.minOrderUnits,
            },
          }
        : {}),
    },
  };
}
